import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Music4 } from "lucide-react";
import { CassetteTape, SmileyDoodle, StarDoodle, ScribbleArrow } from "./doodles";
import { scrollToSection } from "@/config/station";

const lineWrap = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};
const line = {
    hidden: { y: "115%", rotate: 3 },
    show: { y: "0%", rotate: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const MaskedLine = ({ children, className = "" }) => (
    <span className={`block overflow-hidden pb-[0.08em] ${className}`}>
        <motion.span className="block will-change-transform" variants={line}>
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

    return (
        <section
            id="home"
            data-testid="hero-section"
            ref={ref}
            className="relative overflow-hidden bg-cream pb-16 pt-10 sm:pt-14 lg:pb-24"
        >
            <StarDoodle className="absolute left-[6%] top-24 hidden h-6 w-6 text-pink lg:block" fill="#FF70A6" />
            <StarDoodle className="absolute bottom-16 right-[8%] hidden h-8 w-8 lg:block" fill="#3FA9F5" />

            <div className="mx-auto grid max-w-6xl grid-cols-12 items-center gap-6 lg:gap-10 px-4 sm:px-6">
                <div className="col-span-12 min-w-0 lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span data-testid="hero-station-badge" className="sticker bg-sunny">
                            <span className="h-2 w-2 rounded-full bg-ink" />
                            MyRadio1st Presents
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={lineWrap}
                        initial="hidden"
                        animate="show"
                        className="mt-5 font-marker leading-[1.02] tracking-tight text-ink text-[clamp(1.7rem,8.5vw,5.75rem)]"
                    >
                        <MaskedLine>INSPIRATION</MaskedLine>
                        <MaskedLine className="text-pink [-webkit-text-stroke:2px_#1A1918]">
                            STATION
                        </MaskedLine>
                    </motion.h1>

                    <motion.p
                        data-testid="hero-headline"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-4 -rotate-1 font-hand text-3xl font-bold text-ink sm:text-4xl"
                    >
                        "Yo, welcome to my station."
                    </motion.p>

                    <motion.p
                        data-testid="hero-intro-copy"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-4 max-w-xl text-base leading-relaxed text-ink2 sm:text-lg"
                    >
                        What's good? You're at MyRadio1st Presents: Inspiration Station. This is a place
                        for good music, independent artists, fresh ideas, and positive energy. Pull up,
                        press play, and make yourself at home.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-listen-live-button"
                            onClick={() => scrollToSection("listen-live")}
                            className="btn-ink bg-sunny hover:bg-sunnyhover"
                        >
                            <Play className="h-5 w-5 fill-ink" strokeWidth={2.75} />
                            Listen Live
                        </button>
                        <button
                            data-testid="hero-submit-music-button"
                            onClick={() => scrollToSection("submit-music")}
                            className="btn-ink bg-white hover:bg-pinklight"
                        >
                            <Music4 className="h-5 w-5" strokeWidth={2.75} />
                            Submit Your Music
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.6 }}
                        className="mt-6 flex items-center gap-2"
                    >
                        <span className="-rotate-2 font-hand text-xl font-bold text-ink2">
                            good music. good energy. no gatekeeping.
                        </span>
                        <ScribbleArrow className="h-8 w-14 rotate-[160deg]" />
                    </motion.div>
                </div>

                <motion.div
                    className="relative col-span-12 min-w-0 lg:col-span-5"
                    style={{ y, rotate }}
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="card-hard animate-bob bg-creamalt p-5 sm:p-7">
                        <div className="mb-3 flex items-center justify-between">
                            <span className="font-mono2 text-xs font-bold uppercase tracking-[0.18em] text-ink2">
                                Now in the deck
                            </span>
                            <span className="sticker bg-mint !text-[10px]">C-90</span>
                        </div>
                        <CassetteTape spinning />
                        <p className="mt-3 text-center font-hand text-2xl font-bold text-ink2">
                            the good stuff goes here
                        </p>
                    </div>
                    <motion.div
                        className="absolute -left-6 -top-6"
                        animate={{ rotate: [0, 8, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <SmileyDoodle className="h-16 w-16 drop-shadow-[3px_3px_0_#1A1918]" />
                    </motion.div>
                    <StarDoodle className="absolute -right-3 top-10 h-7 w-7" fill="#FFD026" />
                </motion.div>
            </div>
        </section>
    );
}
